// ---------------------------------------------------------------------------
// Game actions — the per-table action bar (and its keybindings).
//
// A table type lists action IDS (shared/tables.ts `actions`); this module
// maps them to labels + behavior. Buttons show the bound key from Settings
// where one exists, so the bar doubles as a cheat sheet.
// ---------------------------------------------------------------------------

import { getDef } from "../shared/defs";
import type { Intent } from "../shared/protocol";
import { getTableSpec } from "../shared/tables";
import type { Replica } from "./replica";
import type { Settings, ActionId } from "./settings";
import type { UI } from "./ui";

export interface ActionCtx {
  send(i: Intent): void;
  state: Replica;
  ui: UI;
  settings: Settings;
  /** Current selection, else the hovered object. */
  targets(): string[];
}

export interface GameAction {
  id: string;
  label: string;
  /** Tooltip; the bound key (if any) is appended automatically. */
  hint: string;
  /** Settings action whose key is shown on the button. */
  kb?: ActionId;
  run(): void;
}

/** The deck on my library slot, else the biggest loose card pile on the table. */
function mainDeck(state: Replica): string | null {
  const slot = [...state.objs.values()].find(
    (o) => o.def === "zone.slot" && o.slot === "library" && o.seat === state.seat,
  );
  if (slot) {
    for (const o of state.objs.values()) {
      if (getDef(o.def).kind === "stack" && !o.heldBy && Math.hypot(o.rp.x - slot.rp.x, o.rp.z - slot.rp.z) < 5) {
        return o.id;
      }
    }
    return null;
  }
  let best: { id: string; n: number } | null = null;
  for (const o of state.objs.values()) {
    const d = getDef(o.def);
    if (d.kind !== "stack" || !d.acceptsGroup?.startsWith("cards") || o.heldBy || o.inZone) continue;
    const n = o.count ?? 1;
    if (!best || n > best.n) best = { id: o.id, n };
  }
  return best?.id ?? null;
}

/** Cards-only filter for "send selected to …" actions. */
function cardTargets(ctx: ActionCtx): string[] {
  return ctx.targets().filter((id) => {
    const o = ctx.state.objs.get(id);
    const k = o ? getDef(o.def).kind : "";
    return k === "card" || k === "stack";
  });
}

function withDeck(ctx: ActionCtx, fn: (id: string) => void): void {
  const id = mainDeck(ctx.state);
  if (!id) ctx.ui.toast("No deck found — put one on your library slot.");
  else fn(id);
}

function sendCards(ctx: ActionCtx, fn: (ids: string[]) => Intent): void {
  const ids = cardTargets(ctx);
  if (ids.length === 0) ctx.ui.toast("Select (or hover) the cards first.");
  else ctx.send(fn(ids));
}

function build(ctx: ActionCtx, dealN: number | undefined): Record<string, GameAction> {
  const A = (id: string, label: string, hint: string, run: () => void, kb?: ActionId): GameAction =>
    ({ id, label, hint, kb, run });
  return Object.fromEntries([
    A("draw", "Draw", "Draw 1 from your deck", () => withDeck(ctx, (id) => ctx.send({ t: "draw", id, n: 1 })), "drawCard"),
    A("draw7", "Draw 7", "Draw your opening hand", () => withDeck(ctx, (id) => ctx.send({ t: "draw", id, n: 7 }))),
    A("mulligan", "Mulligan", "Shuffle your hand into your deck, draw 7", () =>
      ctx.ui.confirm("Shuffle your hand into your deck and draw 7?", () => ctx.send({ t: "mulligan", n: 7 })), "mulligan"),
    A("untap", "Untap ⟳", "New turn: untap your cards + draw 1", () => ctx.send({ t: "newTurn" }), "newTurn"),
    A("toGrave", "→ Grave", "Send selected cards to your graveyard", () =>
      sendCards(ctx, (ids) => ({ t: "toSlot", ids, slot: "graveyard" })), "toGrave"),
    A("toExile", "→ Exile", "Send selected cards to exile", () =>
      sendCards(ctx, (ids) => ({ t: "toSlot", ids, slot: "exile" })), "toExile"),
    A("toHand", "→ Hand", "Return selected cards to your hand", () =>
      sendCards(ctx, (ids) => ({ t: "toHand", ids })), "toHand"),
    A("toInk", "→ Ink", "Ink selected cards (face-down to your inkwell)", () =>
      sendCards(ctx, (ids) => ({ t: "toSlot", ids, slot: "inkwell" }))),
    A("shuffleLib", "Shuffle", "Shuffle your deck", () => withDeck(ctx, (id) => ctx.send({ t: "shuffle", id })), "shuffle"),
    A("prizes", "Prizes ×6", "Set out 6 face-down prizes from your deck", () =>
      withDeck(ctx, (id) => ctx.send({ t: "prizes", id }))),
    A("coin", "Flip coin", "Flip the table's coin", () => {
      const coin = [...ctx.state.objs.values()].find((o) => o.def === "die.coin");
      if (coin) ctx.send({ t: "roll", ids: [coin.id] });
      else ctx.ui.toast("No coin on the table.");
    }),
    A("dealAll", `Deal ${dealN ?? "?"}`, "Deal a hand to every player", () =>
      withDeck(ctx, (id) => ctx.send({ t: "deal", id, n: dealN ?? 1 }))),
    A("deal1", "Deal 1", "Deal 1 card to every player", () => withDeck(ctx, (id) => ctx.send({ t: "deal", id, n: 1 }))),
    A("deal5", "Deal 5", "Deal 5 cards to every player", () => withDeck(ctx, (id) => ctx.send({ t: "deal", id, n: 5 }))),
    A("takeTrick", "Take trick", "Collect the played cards as your trick", () => ctx.send({ t: "takeTrick" })),
    A("sortHand", "Sort hand", "Sort the cards in your hand", () => ctx.send({ t: "sortHand" }), "sortHand"),
  ].map((a) => [a.id, a]));
}

/** The action-bar entries for a table type (empty = no bar). */
export function actionsFor(type: string, ctx: ActionCtx): GameAction[] {
  const spec = getTableSpec(type);
  if (!spec.actions?.length) return [];
  const all = build(ctx, spec.deal);
  return spec.actions.map((id) => all[id]).filter((a): a is GameAction => !!a);
}
